import { useState } from 'react';
import { Badge } from '@/ui/Badge';
import { cn } from '@/lib/cn';
import { Button, estiloDeBoton } from '@/ui/Button';
import { Card } from '@/ui/Card';
import { ConfirmDialog } from '@/ui/Dialog';
import { SkeletonFilas } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { EstadoVacio } from '@/components/EstadoVacio';
import { Icono } from '@/components/Icono';
import { useBorrarPlantilla, usePlantillas, type Plantilla } from './api';
import { FormularioDePlantilla } from './FormularioDePlantilla';

/**
 * Plantillas — los formatos PDF que Bienestar publica.
 *
 * ## Lo que cambia
 *
 * - **Configurar una plantilla tiene dirección**: `#documentos/plantilla/<id>`.
 *   El vanilla repintaba el contenedor guardando la plantilla abierta en `_cfg`,
 *   una variable de módulo: el Atrás salía de la pantalla y no se podía enlazar
 *   una plantilla concreta.
 * - **«sin PDF» y «sin campos» se dicen los dos.** El vanilla avisaba de lo
 *   primero y callaba lo segundo, y una plantilla publicada sin campos genera un
 *   PDF sin estampar nada — que es el estado real de las dos publicadas que hay
 *   en la base.
 */
export function Plantillas({ activa }: { activa: boolean }) {
  const consulta = usePlantillas(activa);
  const borrar = useBorrarPlantilla();
  const [editando, setEditando] = useState<Plantilla | null | undefined>(undefined);
  const [borrando, setBorrando] = useState<Plantilla | null>(null);

  const plantillas = consulta.data ?? [];

  return (
    <>
      <div className="mb-3 flex justify-end">
        <Button size="sm" onClick={() => setEditando(null)}>
          <i className="bi bi-plus-circle-fill" aria-hidden="true" /> Nueva plantilla
        </Button>
      </div>

      {consulta.isPending ? (
        <SkeletonFilas filas={4} columnas={3} />
      ) : consulta.isError ? (
        <EstadoError
          err={consulta.error}
          que="las plantillas"
          onReintentar={() => void consulta.refetch()}
        />
      ) : !plantillas.length ? (
        <EstadoVacio
          icono="bi-files"
          titulo="Sin plantillas"
          descripcion="Crea la primera: se sube el PDF oficial y se colocan los campos encima."
          accion={{ texto: 'Nueva plantilla', onClick: () => setEditando(null) }}
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {plantillas.map((t) => (
            <li key={t.id}>
              <Card className="flex flex-wrap items-center gap-3 p-3">
                <span className="shrink-0 text-7 text-brand-ink" aria-hidden="true">
                  <Icono nombre={t.icon} respaldo="bi-file-earmark-text" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-center gap-2">
                    <strong className="text-3 text-ink">{t.name || '(sin nombre)'}</strong>
                    <Badge tone={t.status === 'publicado' ? 'ok' : 'warn'}>{t.status}</Badge>
                    {!t.has_pdf && (
                      <Badge tone="danger">
                        <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" /> sin PDF
                      </Badge>
                    )}
                    {/* Una plantilla publicada y sin campos genera un PDF sin
                        estampar nada. El vanilla no lo decía en ninguna parte. */}
                    {t.has_pdf && t.field_count === 0 && (
                      <Badge tone="warn">
                        <i className="bi bi-exclamation-triangle" aria-hidden="true" /> sin campos
                      </Badge>
                    )}
                  </p>
                  <p className="text-1 text-muted">
                    {t.field_count} {t.field_count === 1 ? 'campo' : 'campos'} · {t.page_count}{' '}
                    {t.page_count === 1 ? 'pág.' : 'págs.'}
                  </p>
                </div>
                <a
                  href={`#documentos/plantilla/${t.id}`}
                  className={cn(estiloDeBoton({ variant: 'secondary', size: 'sm' }), 'no-underline')}
                >
                  <i className="bi bi-sliders" aria-hidden="true" /> Configurar
                </a>
                <Button
                  variant="ghost"
                  size="sm"
                  iconOnly
                  aria-label={`Eliminar ${t.name || 'la plantilla'}`}
                  onClick={() => setBorrando(t)}
                >
                  <i className="bi bi-trash text-danger-ink" aria-hidden="true" />
                </Button>
              </Card>
            </li>
          ))}
        </ul>
      )}

      {editando !== undefined && (
        <FormularioDePlantilla
          plantilla={editando}
          onCerrar={() => setEditando(undefined)}
          onCreada={(id) => { location.hash = `#documentos/plantilla/${id}`; }}
        />
      )}

      <ConfirmDialog
        abierto={borrando != null}
        onCambio={(v) => { if (!v) setBorrando(null); }}
        titulo="Eliminar la plantilla"
        descripcion={`«${borrando?.name || 'Sin nombre'}» deja de ofrecerse y de listarse. Los radicados que ya salieron de ella se conservan: son evidencia.`}
        textoConfirmar="Eliminar"
        destructivo
        cargando={borrar.isPending}
        onConfirmar={() => {
          if (!borrando) return;
          borrar.mutate(borrando.id, {
            onSuccess: () => { Utils.toast('Plantilla eliminada', 'success'); setBorrando(null); },
            onError: (err) => {
              setBorrando(null);
              Utils.toast(err instanceof Error ? err.message : 'No se pudo eliminar', 'error');
            },
          });
        }}
      />
    </>
  );
}
